import React from "react";
import { Card, Alert } from "react-bootstrap";

function WorkflowBuilder() {
  return (
    <div>
      <h2 className="mb-4">Workflow Builder</h2>

      <Alert variant="info">
        The Workflow Builder is a visual tool for designing runbook workflows by dragging and connecting steps.
      </Alert>

      <Card className="mb-4">
        <Card.Body>
          <h4>Opening the Workflow Builder</h4>
          <ol>
            <li>Go to <strong>My Runbooks</strong></li>
            <li>Select a runbook</li>
            <li>Click the <strong>Workflow</strong> button or icon</li>
            <li>The Workflow Builder will open showing the runbook's workflow</li>
          </ol>
        </Card.Body>
      </Card>

      <Card className="mb-4">
        <Card.Body>
          <h4>Workflow Builder Interface</h4>
          <p>The Workflow Builder has three main areas:</p>
          <ul>
            <li><strong>Canvas</strong> - The main area where you design your workflow</li>
            <li><strong>Step Palette</strong> - On the left, showing available step types you can add</li>
            <li><strong>Properties Panel</strong> - On the right, showing configuration options for the selected step</li>
          </ul>
        </Card.Body>
      </Card>

      <Card className="mb-4">
        <Card.Body>
          <h4>Adding Steps to Your Workflow</h4>
          <ol>
            <li>Browse the Step Palette on the left to find the step type you want</li>
            <li>Drag the step from the palette onto the canvas</li>
            <li>Drop it where you want it in your workflow</li>
            <li>Click on the step to select it</li>
            <li>Configure the step using the Properties Panel on the right</li>
          </ol>
        </Card.Body>
      </Card>

      <Card className="mb-4">
        <Card.Body>
          <h4>Connecting Steps</h4>
          <p>
            Steps in a workflow execute in sequence. To define the execution order:
          </p>
          <ol>
            <li>Click on a step's connection point (usually on the right or bottom)</li>
            <li>Drag to create a connector line</li>
            <li>Drop the connector on the next step's input point</li>
            <li>The steps are now connected and will execute in order</li>
          </ol>
        </Card.Body>
      </Card>

      <Card className="mb-4">
        <Card.Body>
          <h4>Configuring Step Parameters</h4>
          <p>When you select a step, the Properties Panel shows available configuration options:</p>
          <ul>
            <li><strong>Step Name</strong> - A label for the step</li>
            <li><strong>Connector</strong> - Which connector to use (if the step needs one)</li>
            <li><strong>Variables</strong> - Which variables to read from or write to</li>
            <li><strong>Step-specific settings</strong> - Different for each step type</li>
          </ul>
        </Card.Body>
      </Card>

      <Card className="mb-4">
        <Card.Body>
          <h4>Common Step Types</h4>
          <ul>
            <li><strong>API Call</strong> - Make requests to external APIs</li>
            <li><strong>Data Transform</strong> - Process and modify data</li>
            <li><strong>Conditional</strong> - Execute different paths based on conditions</li>
            <li><strong>Loop</strong> - Repeat steps for each item in a data set</li>
            <li><strong>Variable Read/Write</strong> - Access stored data</li>
            <li><strong>Notification</strong> - Send emails or alerts</li>
          </ul>
        </Card.Body>
      </Card>

      <Card className="mb-4">
        <Card.Body>
          <h4>Saving Your Workflow</h4>
          <ol>
            <li>After designing your workflow, click the <strong>Save</strong> button</li>
            <li>The workflow will be validated automatically</li>
            <li>If validation passes, your changes are saved</li>
            <li>If validation fails, review the error messages and fix any issues</li>
          </ol>
        </Card.Body>
      </Card>

      <Card className="mb-4">
        <Card.Body>
          <h4>Import/Export Workflows</h4>
          <p>
            You can export a workflow as JSON and import it into another runbook:
          </p>
          <ul>
            <li>Click <strong>Export</strong> to download the workflow as a JSON file</li>
            <li>Click <strong>Import</strong> to load a workflow from a JSON file</li>
          </ul>
          <p className="mt-2">
            <em>This is useful for backing up workflows or copying them between runbooks.</em>
          </p>
        </Card.Body>
      </Card>

      <Card className="mb-4">
        <Card.Body>
          <h4>Best Practices</h4>
          <ul>
            <li>Give each step a clear, descriptive name</li>
            <li>Test your workflow frequently during development</li>
            <li>Use variables to avoid hardcoding values</li>
            <li>Add error handling steps for critical operations</li>
            <li>Document complex workflows with comments or descriptions</li>
          </ul>
        </Card.Body>
      </Card>
    </div>
  );
}

export default WorkflowBuilder;
